// frontend/src/lib/errors/pocketbase.ts

import { captureException } from "@sentry/react";
import { ClientResponseError } from "pocketbase";
import { z } from "zod";
import { isEmpty } from "../utils";
import { translateErrorCode } from "./translate";

const SERVER_ERROR_STATUS = 500;

function removeTrailingDot(text: string) {
	return text.endsWith(".") ? text.slice(0, -1) : text;
}

// --- Zod schemas ---

const pbFieldErrorSchema = z.object({
	code: z.string(),
	message: z.string(),
});

const batchRequestErrorSchema = z.object({
	code: z.string(),
	message: z.string(),
	response: z.object({
		message: z.string(),
		status: z.number(),
		data: z.record(z.string(), pbFieldErrorSchema),
	}),
});

const batchErrorDataSchema = z.object({
	requests: z.record(z.string(), batchRequestErrorSchema),
});

const pbErrorResponseSchema = z.object({
	data: z.record(z.string(), z.unknown()),
});

// --- Types ---

export type BlockerDetail = {
	relation: string;
	message: string;
};

export type BatchItemError = {
	index: number;
	code: string;
	message: string;
	blockers: Array<BlockerDetail>;
};

type ErrorMap = {
	fields: { [key: string]: { message: string } };
	batchErrors: Array<BatchItemError>;
};

type ParsedPocketBaseError = {
	errorMap: ErrorMap;
	unknown: boolean;
	status: number;
	message: string;
};

type FieldMapping = Record<string, unknown>;

// --- Type guards ---

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

// --- Helpers ---

function getNestedValue(obj: unknown, path: string): unknown {
	const keys = path.split(".");
	let current: unknown = obj;

	for (const key of keys) {
		if (isRecord(current) && key in current) {
			current = current[key];
		} else {
			return undefined;
		}
	}

	return current;
}

function emptyErrorMap(): ErrorMap {
	return { fields: {}, batchErrors: [] };
}

function parseFieldErrors(data: Record<string, unknown>, formFieldNames: Array<string>): ErrorMap {
	const result = emptyErrorMap();

	// Check for batch error shape: { requests: { "0": {...} } }
	const batchParsed = batchErrorDataSchema.safeParse(data);
	if (batchParsed.success) {
		const requests = batchParsed.data.requests;
		for (const index of Object.keys(requests)) {
			const req = requests[index];
			if (!req) continue;

			const msg = removeTrailingDot(req.response.message);

			// Extract blocker details from response.data
			// Keys are like "procedures.patient", values have { code, message }
			const blockers: Array<BlockerDetail> = [];
			const responseData = req.response.data;
			for (const relation of Object.keys(responseData)) {
				const detail = responseData[relation];
				if (!detail) continue;
				blockers.push({
					relation,
					message: removeTrailingDot(detail.message),
				});
			}

			result.batchErrors.push({
				index: Number(index),
				code: req.code,
				message: translateErrorCode(msg),
				blockers,
			});
		}
		return result;
	}

	// Existing field error parsing
	for (const formField of formFieldNames) {
		const raw = getNestedValue(data, formField);
		const parsed = pbFieldErrorSchema.safeParse(raw);
		if (parsed.success) {
			result.fields[formField] = {
				message: translateErrorCode(parsed.data.code) || parsed.data.message,
			};
		}
	}

	return result;
}

// Raw (untranslated) field error code, for callers that need to branch on the specific
// PocketBase code (e.g. "validation_not_unique") rather than just show translated text.
export function getFieldErrorCode(error: unknown, field: string): string | undefined {
	if (!(error instanceof ClientResponseError)) {
		return;
	}

	const responseParsed = pbErrorResponseSchema.safeParse(error.response);
	if (!responseParsed.success) {
		return;
	}

	const fieldParsed = pbFieldErrorSchema.safeParse(responseParsed.data.data[field]);
	return fieldParsed.success ? fieldParsed.data.code : undefined;
}

export function parsePocketBaseError(error: unknown, fieldMapping: FieldMapping = {}): ParsedPocketBaseError {
	const formFieldNames = Object.keys(fieldMapping);

	if (!(error instanceof ClientResponseError)) {
		captureException(error);
		const message = error instanceof Error && error.message;

		return {
			errorMap: emptyErrorMap(),
			unknown: true,
			status: 500,
			message: message || "An unknown error occurred",
		};
	}

	const status = error.status;

	if (status >= SERVER_ERROR_STATUS) {
		captureException(error);
	}

	const messageWithoutEndDot = removeTrailingDot(error.message);
	const message = translateErrorCode(messageWithoutEndDot);

	// Validate error.response through zod — avoids assigning .data (which is any)
	const responseParsed = pbErrorResponseSchema.safeParse(error.response);
	if (!responseParsed.success) {
		return {
			errorMap: emptyErrorMap(),
			unknown: true,
			status,
			message,
		};
	}

	const responseData = responseParsed.data.data;
	const errorMap = parseFieldErrors(responseData, formFieldNames);

	const unknown = isEmpty(errorMap.fields) && errorMap.batchErrors.length === 0;

	return {
		errorMap,
		unknown,
		status,
		message,
	};
}
